/**
 * House Loan Results Display Component
 * Shows calculated mortgage payment details
 */
import { useState } from 'react';
import { useCustomTranslation } from '../../../hooks';
import { formatCurrency, formatPercentage } from '../../../utils/formatters';
import { generateHouseLoanPDF } from '../../../utils/pdfGenerator';
import { trackPDFDownload, trackConversion } from '../../../utils/analytics';
import type { HouseLoanResults } from './HouseLoanCalculatorForm';

interface HouseLoanResultsProps {
  results: HouseLoanResults;
}

export default function HouseLoanResults({ results }: HouseLoanResultsProps) {
  const { t, i18n } = useCustomTranslation();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    try {
      generateHouseLoanPDF({
        loanAmount: results.loan_amount,
        interestRate: results.annual_interest_rate,
        loanTermYears: results.loan_term_years,
        monthlyPayment: results.monthly_payment,
        totalPayment: results.total_payment,
        totalInterest: results.total_interest,
        language: i18n.language
      });
      
      // Track PDF download
      trackPDFDownload({
        calculator_type: 'house_loan',
        language: i18n.language,
      });
      
      // Track conversion
      trackConversion('pdf_download_house_loan');
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      // Reset button state after a short delay
      setTimeout(() => setIsGeneratingPDF(false), 1000);
    }
  };

  return (
    <div style={{ 
      maxWidth: '600px', 
      padding: '20px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    }}>
      <div style={{ 
        display: 'grid', 
        gap: '20px'
      }}>
        {/* Monthly Payment - Highlighted */}
        <div style={{
          padding: '20px',
          backgroundColor: '#dbeafe',
          borderRadius: '8px',
          border: '2px solid #2563eb'
        }}>
          <div style={{ 
            fontSize: '14px', 
            color: '#1e40af',
            marginBottom: '8px',
            fontWeight: '500'
          }}>
            {t('houseLoan.monthlyPayment')}
          </div>
          <div style={{ 
            fontSize: '32px', 
            fontWeight: 'bold',
            color: '#1e40af'
          }}>
            {formatCurrency(results.monthly_payment)}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#64748b',
            marginTop: '4px'
          }}>
            {t('houseLoan.perMonth')} × {results.loan_term_months} {t('houseLoan.months')}
          </div>
        </div>

        {/* Loan Details */}
        <div style={{
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ 
            display: 'grid', 
            gap: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>{t('houseLoan.loanAmount')}</span>
              <span style={{ fontWeight: '600' }}>{formatCurrency(results.loan_amount)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>{t('houseLoan.interestRate')}</span>
              <span style={{ fontWeight: '600' }}>{formatPercentage(results.annual_interest_rate)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>{t('houseLoan.loanTerm')}</span>
              <span style={{ fontWeight: '600' }}>{results.loan_term_years} {t('houseLoan.years')}</span>
            </div>
          </div>
        </div>

        {/* Total Cost Breakdown */}
        <div style={{
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            marginTop: 0, 
            marginBottom: '15px',
            fontSize: '18px',
            color: '#1e293b'
          }}>
            {t('houseLoan.totalCost')}
          </h3>
          <div style={{ 
            display: 'grid', 
            gap: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748b' }}>{t('houseLoan.totalPayment')}</span>
              <span style={{ fontWeight: '600' }}>{formatCurrency(results.total_payment)}</span>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              paddingTop: '12px',
              borderTop: '1px solid #e2e8f0'
            }}>
              <span style={{ color: '#64748b' }}>{t('houseLoan.totalInterest')}</span>
              <span style={{ fontWeight: '600', color: '#dc2626' }}>
                {formatCurrency(results.total_interest)}
              </span>
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#64748b',
              marginTop: '8px',
              padding: '12px',
              backgroundColor: '#fef3c7',
              borderRadius: '4px'
            }}>
              💡 {t('houseLoan.interestNote', { 
                percentage: ((results.total_interest / results.loan_amount) * 100).toFixed(1) 
              })}
            </div>
          </div>
        </div>

        {/* Download PDF Button */}
        <button
          onClick={handleDownloadPDF}
          disabled={isGeneratingPDF}
          style={{
            padding: '14px 24px',
            backgroundColor: isGeneratingPDF ? '#94a3b8' : '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%'
          }}
          onMouseEnter={(e) => {
            if (!isGeneratingPDF) {
              e.currentTarget.style.backgroundColor = '#059669';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isGeneratingPDF) {
              e.currentTarget.style.backgroundColor = '#10b981';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          {isGeneratingPDF ? t('houseLoan.downloadingPDF') : t('houseLoan.downloadPDF')}
        </button>

        {/* Quick Tips */}
        <div style={{
          padding: '16px',
          backgroundColor: '#dcfce7',
          borderRadius: '8px',
          border: '1px solid #10b981',
          fontSize: '14px'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '8px', color: '#065f46' }}>
            💡 {t('houseLoan.tips')}
          </div>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#064e3b' }}>
            <li>{t('houseLoan.tip1')}</li>
            <li>{t('houseLoan.tip2')}</li>
            <li>{t('houseLoan.tip3')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
